import { useState } from "react";
import { Sparkles, Loader2, X } from "lucide-react";
import { api, describeApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Shows an inline AI-name brainstorm panel.
 * Props:
 *   theme: current theme id (required for the API)
 *   onPick({name, ticker, tagline}): callback when user clicks a suggestion
 */
export default function NameSuggester({ theme, onPick }) {
  const [open, setOpen] = useState(false);
  const [vibe, setVibe] = useState("");
  const [names, setNames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const run = async () => {
    setBusy(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/ai/suggest-names", {
        theme,
        vibe: vibe.trim() || null,
      });
      setNames(data.names || []);
      if (!data.names?.length) {
        setErrMsg("AI returned no names — try again or change the vibe hint");
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "";
      if (status === 503 || /not configured|access_denied|FREE_USER/i.test(detail)) {
        setErrMsg("AI is temporarily unavailable. No problem — just type your own name above. You can publish without AI.");
      } else {
        setErrMsg(describeApiError(e, "Couldn't suggest names. Try again or type your own."));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="suggest-names-open"
        className="mt-3 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#cfff04] hover:underline"
      >
        <Sparkles size={12} /> Stuck? Let AI brainstorm 8 names →
      </button>
    );
  }

  return (
    <div className="mt-3 border border-[#cfff04] bg-[#121212] p-5" data-testid="suggest-names-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#cfff04]">
          // AI Brainstorm · theme: {theme}
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[#d0d0d0] hover:text-white"
          aria-label="close"
          data-testid="suggest-names-close"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          value={vibe}
          onChange={(e) => setVibe(e.target.value)}
          placeholder="Vibe hint (optional) — e.g. 'cute ocean animals'"
          maxLength={80}
          data-testid="suggest-vibe"
          className="flex-1 bg-[#050505] border border-[#3d3d3d] focus:border-[#cfff04] focus:outline-none placeholder:text-[#b8b8b8] px-3 py-2 text-sm font-mono"
        />
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="suggest-names-run"
          className="btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-xs px-4 flex items-center gap-2"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {names.length ? "Re-roll" : "Generate"}
        </button>
      </div>

      {names.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="suggest-names-grid">
          {names.map((n, i) => (
            <button
              type="button"
              key={`${n.name}-${n.ticker}`}
              onClick={() => {
                onPick(n);
                setOpen(false);
                toast.success(`Picked ${n.name}`);
              }}
              data-testid={`suggest-name-${i}`}
              className="text-left border border-[#3a3a3a] bg-[#050505] hover:border-[#cfff04] hover:bg-[#121212] p-3 transition-colors"
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-display text-base font-bold uppercase tracking-tight">
                  {n.name}
                </div>
                <div className="font-mono text-[11px] text-[#cfff04] uppercase tracking-widest">
                  ${n.ticker}
                </div>
              </div>
              <div className="text-sm text-[#c4c4c4] mt-1 line-clamp-2">{n.tagline}</div>
            </button>
          ))}
        </div>
      ) : busy ? (
        <div className="text-center py-6 text-[#d0d0d0] font-mono text-xs uppercase tracking-widest">
          Brainstorming…
        </div>
      ) : errMsg ? (
        <div
          data-testid="suggest-names-error"
          className="border border-yellow-700/40 bg-yellow-900/10 p-3 text-sm leading-relaxed text-yellow-200"
        >
          {errMsg}
        </div>
      ) : (
        <div className="text-center py-6 text-[#d0d0d0] font-mono text-xs uppercase tracking-widest">
          // hit Generate to see 8 ideas
        </div>
      )}
    </div>
  );
}
